import React, { useState, useCallback, useEffect } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import * as Accordion from "@radix-ui/react-accordion";
import {
  FaSearch,
  FaCalendarAlt,
  FaSortAmountDown,
  FaChevronDown,
  FaSync,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getLogs } from "../../API/getLogs";
import * as Progress from "@radix-ui/react-progress";

const logTypes = ["app", "error", "access", "security"];

const SingleDatePicker = ({ selectedDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const minDate = new Date();
  minDate.setDate(minDate.getDate() - 13);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 flex items-center"
      >
        <FaCalendarAlt className="mr-2" />
        <span>{selectedDate.toLocaleDateString()}</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 z-50 bg-white rounded-lg shadow-xl p-4">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              onDateChange(date);
              setIsOpen(false);
            }}
            minDate={minDate}
            maxDate={new Date()}
            inline
          />
        </div>
      )}
    </div>
  );
};
const getStatusColor = (status) => {
  if (status === "Unknown") return "bg-purple-100 text-purple-800";
  if (status >= 500) return "bg-red-100 text-red-800";
  if (status >= 400) return "bg-yellow-100 text-yellow-800";
  if (status >= 300) return "bg-blue-100 text-blue-800";
  if (status >= 200) return "bg-green-100 text-green-800";
  return "bg-gray-100 text-gray-800";
};

const parseLogMessage = (message) => {
  const regex = /^(\S+) - - \[(.*?)\] "(.*?)" (\S+) (\S+) "(.*?)" "(.*?)"$/;
  const match = message.match(regex);
  if (match) {
    const [, ip, timestamp, request, status, bytes, referer, userAgent] = match;
    const [method, url] = request.split(" ");
    return {
      ip,
      timestamp,
      method,
      url,
      status: status === "-" ? "Unknown" : status,
      bytes,
      referer,
      userAgent,
    };
  }
  return null;
};
const TimeRangeSelector = ({ timeRange, setTimeRange, isToday }) => {
  const ranges = [1, 2, 5, 12, 16];

  return (
    <select
      value={timeRange}
      onChange={(e) => setTimeRange(Number(e.target.value))}
      className={`p-2 rounded ${
        isToday
          ? "bg-blue-500 text-white hover:bg-blue-600"
          : "bg-gray-300 text-gray-500 cursor-not-allowed"
      }`}
      disabled={!isToday}
    >
      {ranges.map((range) => (
        <option key={range} value={range}>
          {range} hour{range > 1 ? "s" : ""}
        </option>
      ))}
    </select>
  );
};

const LoadingScreen = () => (
  <div className="flex flex-col items-center justify-center h-64">
    <Progress.Root
      className="w-[300px] h-[25px] overflow-hidden bg-gray-200 rounded-full"
      value={33}
    >
      <Progress.Indicator
        className="w-full h-full bg-blue-500 transition-transform duration-[660ms] ease-[cubic-bezier(0.65, 0, 0.35, 1)]"
        style={{ transform: `translateX(-${100 - 33}%)` }}
      />
    </Progress.Root>
    <p className="mt-4 text-gray-600">Loading logs...</p>
  </div>
);
const MapsLogs = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isToday, setIsToday] = useState(true);
  const [logs, setLogs] = useState([]);
  const [activeLogType, setActiveLogType] = useState("app");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "descending",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(1); // Default to 1 hour
  const filterLogsByTimeRange = useCallback((logs, range, selectedDate) => {
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();

    if (!isToday) {
      return logs; // Return all logs for the selected date if it's not today
    }

    const now = new Date();
    const rangeStart = new Date(now.getTime() - range * 60 * 60 * 1000);
    return logs.filter((log) => new Date(log.timestamp) >= rangeStart);
  }, []);
  const fetchLogs = useCallback(
    async (date = selectedDate) => {
      setIsLoading(true);
      setLogs([]); // Clear current logs
      const result = await getLogs(activeLogType, date);
      if (result.success) {
        setLogs(result.logs);
        setIsToday(date.toDateString() === new Date().toDateString());
      } else {
        console.error(result.message);
      }
      setIsLoading(false);
    },
    [activeLogType, selectedDate]
  );
  useEffect(() => {
    fetchLogs(selectedDate);
  }, [fetchLogs, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setIsToday(date.toDateString() === new Date().toDateString());
    fetchLogs(date);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const sortedLogs = useCallback(() => {
    const filteredByTime = filterLogsByTimeRange(logs, timeRange, selectedDate);
    return filteredByTime.sort((a, b) => {
      if (sortConfig.key === "timestamp") {
        return new Date(b.timestamp) - new Date(a.timestamp);
      }
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === "ascending" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [logs, sortConfig, timeRange, filterLogsByTimeRange, selectedDate]);
  const filteredLogs = logs.filter((log) =>
    Object.values(log).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="bg-gray-100 py-10 px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-700">System Logs</h2>
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <SingleDatePicker
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
            <TimeRangeSelector
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              isToday={isToday}
            />
            <button
              onClick={() => fetchLogs(selectedDate)}
              className="bg-green-500 text-white p-2 rounded hover:bg-green-600"
            >
              <FaSync />
            </button>
          </div>
        </div>
        <Tabs.Root value={activeLogType} onValueChange={setActiveLogType}>
          <Tabs.List className="flex space-x-2 mb-6">
            {logTypes.map((logType) => (
              <Tabs.Trigger
                key={logType}
                value={logType}
                className={`px-4 py-2 rounded-lg transition duration-200 ${
                  activeLogType === logType
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {logType.charAt(0).toUpperCase() + logType.slice(1)} Logs
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <div className="bg-white shadow-lg rounded-lg mb-4">
            <div className="grid grid-cols-[auto,1fr,1fr,auto] items-center bg-gray-200 p-2 rounded-t-lg text-sm font-semibold text-gray-700">
              <span>Level</span>
              <button
                onClick={() => handleSort("message")}
                className="flex items-center"
              >
                Message{" "}
                {sortConfig.key === "message" && (
                  <FaSortAmountDown className="ml-1" />
                )}
              </button>
              <button
                onClick={() => handleSort("timestamp")}
                className="flex items-center justify-end"
              >
                Timestamp{" "}
                {sortConfig.key === "timestamp" && (
                  <FaSortAmountDown className="ml-1" />
                )}
              </button>
              <span className="justify-self-end">Details</span>
            </div>
            {isLoading ? (
              <LoadingScreen />
            ) : (
              <Accordion.Root type="single" collapsible>
                {sortedLogs()
                  .filter((log) =>
                    Object.values(log).some((value) =>
                      value
                        .toString()
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    )
                  )
                  .map((log, index) => (
                    <Accordion.Item key={index} value={`item-${index}`}>
                      <Accordion.Header>
                        <Accordion.Trigger className="w-full grid grid-cols-[auto,1fr,1fr,auto] items-center text-left text-xs font-normal transition-colors px-2 py-2 hover:bg-gray-100">
                          {(() => {
                            const parsedLog = parseLogMessage(log.message);

                            if (parsedLog) {
                              const { method, url, status, timestamp } =
                                parsedLog;

                              return (
                                <>
                                  <span
                                    className={`px-2 py-1 rounded ${getStatusColor(
                                      parsedLog.status
                                    )}`}
                                  >
                                    {parsedLog.status}
                                  </span>
                                  <span className="truncate ml-2 font-bold text-green-700">
                                    {method} {url}
                                  </span>
                                  <span className="text-right">
                                    {new Date(
                                      timestamp.replace(":", " ")
                                    ).toLocaleString()}
                                  </span>
                                </>
                              );
                            }

                            return (
                              <>
                                <span className="px-2 py-1 rounded bg-gray-100 text-gray-800">
                                  INFO
                                </span>
                                <span className="truncate ml-2">
                                  {log.message}
                                </span>
                                <span className="text-right">
                                  {log.timestamp
                                    ? new Date(log.timestamp).toLocaleString()
                                    : ""}
                                </span>
                              </>
                            );
                          })()}
                          <FaChevronDown className="justify-self-end" />
                        </Accordion.Trigger>
                      </Accordion.Header>

                      <Accordion.Content className="px-4 py-2 bg-gray-50">
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(log).map(([key, value]) => (
                            <div
                              key={key}
                              className="border-b border-gray-200 pb-2"
                            >
                              <h4 className="font-semibold text-gray-700 capitalize">
                                {key}:
                              </h4>
                              {key === "timestamp" ? (
                                <p>{new Date(value).toLocaleString()}</p>
                              ) : key === "stack" ? (
                                <pre className="whitespace-pre-wrap text-sm bg-gray-100 p-2 rounded mt-1 max-h-40 overflow-y-auto">
                                  {value}
                                </pre>
                              ) : typeof value === "object" ? (
                                <pre className="text-sm bg-gray-100 p-2 rounded mt-1 max-h-40 overflow-y-auto">
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              ) : (
                                <p className="break-words">
                                  {value.toString()}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </Accordion.Content>
                    </Accordion.Item>
                  ))}
              </Accordion.Root>
            )}
          </div>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default MapsLogs;
